import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import DataDisplay, {
  useResponsiveDataDisplayVariant,
} from '@sharedCR/coreDesignSystem/DataDisplay';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import { ValueStub } from 'constants/data';
import usePopover from 'hooks/usePopover';

import { selectChattelDetails } from 'store/loanApplications/selectors';

import ActionMenu from '../ActionMenu';
import { ENTITY_TYPE_HREF_MAP } from '../EditModal/EditModal.constants';
import { PropertyAndLoanTestIds } from '../PropertyAndLoan.testIds';

import { getChattelGroupsConfig } from './ChattelDetailsSection.config';
import {
  CHATTEL_DETAILS_SECTION_TITLE,
  CHATTEL_GROUP_SKELETONS_LENGTH,
} from './ChattelDetailsSection.constants';
import {
  cardStyles,
  getGroupWrapperStyles,
  groupTitleStyles,
  groupWrapperOuterStyles,
  pageBlockClasses,
} from './ChattelDetailsSection.styles';

const ChattelDetailsSection = ({ isLoading }) => {
  const chattelDetails = useSelector(selectChattelDetails);

  const dataDisplayVariant = useResponsiveDataDisplayVariant();

  const groups = useMemo(() => getChattelGroupsConfig(chattelDetails), [chattelDetails]);

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  return (
    <PageBlock
      withAction
      actionContent={
        <MoreButtonCDS
          data-testid={PropertyAndLoanTestIds.CHATTEL_DETAILS_MORE_BUTTON}
          isSelected={!!anchorEl}
          onClick={onSetAnchorElement}
        />
      }
      classes={pageBlockClasses}
      title={CHATTEL_DETAILS_SECTION_TITLE}
    >
      <Box sx={groupWrapperOuterStyles}>
        {groups.map(({ title, cards }) => (
          <Box key={title}>
            <Typography sx={groupTitleStyles} variant="bold16">
              {title}
            </Typography>
            <Box sx={getGroupWrapperStyles(cards.length)}>
              {isLoading
                ? Array.from({ length: CHATTEL_GROUP_SKELETONS_LENGTH }, (_, index) => (
                    <Box key={index} sx={cardStyles}>
                      <DataDisplay isLoading title="" value="" variant={dataDisplayVariant} />
                    </Box>
                  ))
                : cards.map(({ name, value, icon, iconColor, noTruncate }) => (
                    <Box key={name} sx={cardStyles}>
                      <DataDisplay
                        icon={icon}
                        iconColor={iconColor}
                        noTruncate={noTruncate}
                        title={name}
                        value={value ?? ValueStub.COMMON}
                        variant={dataDisplayVariant}
                      />
                    </Box>
                  ))}
            </Box>
          </Box>
        ))}
      </Box>

      <ActionMenu
        anchorEl={anchorEl}
        editText="Edit Chattel Details"
        entityType={ENTITY_TYPE_HREF_MAP.CHATTEL}
        onClearAnchorElement={onClearAnchorElement}
      />
    </PageBlock>
  );
};

ChattelDetailsSection.propTypes = {
  ...WithLoadingPropTypes,
};

export default ChattelDetailsSection;
